import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, Button, Input, Table, Tag} from 'antd';
import {GET_COMPLY_CUBE_CHECKS} from '../resolvers';
import {
  badgeToneForStatus,
  buildChecksInput,
  formatCheckTypeLabel,
  formatDate,
  unwrapListResponse,
  COMPLY_CUBE_CHECKS_KEY,
} from '../kycComplyCubeUtils';
import {
  ComplyCubeTablePagination,
  ComplyCubeTruncatedClientId,
  ComplyCubeUserCell,
} from './ComplyCubeTableShared';
import styles from '../kycDashboard.module.scss';

export function ComplyCubeChecksTab({openClientDetail}) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchDraft, setSearchDraft] = useState('');
  const [clientIdDraft, setClientIdDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedClientId, setAppliedClientId] = useState('');

  const showReset =
    searchDraft.trim() !== '' ||
    clientIdDraft.trim() !== '' ||
    appliedSearch.trim() !== '' ||
    appliedClientId.trim() !== '';

  const handleApply = () => {
    setAppliedSearch(searchDraft);
    setAppliedClientId(clientIdDraft.trim());
    setPage(1);
  };

  const handleReset = () => {
    setSearchDraft('');
    setClientIdDraft('');
    setAppliedSearch('');
    setAppliedClientId('');
    setPage(1);
  };

  const variables = useMemo(
    () => ({
      input: buildChecksInput(page, limit, {
        search: appliedSearch,
        clientId: appliedClientId,
        type: '',
        status: '',
        outcome: '',
      }),
    }),
    [page, limit, appliedSearch, appliedClientId],
  );

  const {data, loading} = useQuery(GET_COMPLY_CUBE_CHECKS, {
    variables,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const {error, rows, total} = unwrapListResponse(
    data,
    COMPLY_CUBE_CHECKS_KEY,
    'ResponseWithComplyCubeChecks',
  );

  return (
    <>
      {error ? (
        <Alert
          type="error"
          message={error.message || 'Request failed'}
          showIcon
        />
      ) : null}
      <div className={styles.viewerFilters}>
        <Input
          allowClear
          placeholder="Search…"
          className={styles.viewerSearch}
          value={searchDraft}
          onChange={e => setSearchDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <Input
          allowClear
          placeholder="Client ID"
          className={styles.viewerFilterInput}
          value={clientIdDraft}
          onChange={e => setClientIdDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <div className={styles.viewerFilterActions}>
          <Button type="primary" onClick={handleApply}>
            Submit
          </Button>
          {showReset ? <Button onClick={handleReset}>Reset</Button> : null}
        </div>
      </div>
      <Table
        className="antd-table"
        loading={loading}
        pagination={false}
        rowKey={r => r.id || r._id}
        dataSource={rows}
        onRow={record => ({
          onClick: () => {
            const cid = record?.clientId;
            if (cid) openClientDetail(cid);
          },
          className: styles.clickableRow,
        })}
        columns={[
          {
            title: 'User',
            key: 'user',
            width: 228,
            render: (_, r) => <ComplyCubeUserCell user={r?.user} />,
          },
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: v => formatCheckTypeLabel(v),
          },
          {
            title: 'Client ID',
            dataIndex: 'clientId',
            key: 'clientId',
            render: id => <ComplyCubeTruncatedClientId id={id} />,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: v => <Tag color={badgeToneForStatus(v)}>{v || '-'}</Tag>,
          },
          {
            title: 'Outcome',
            key: 'outcome',
            render: (_, r) => {
              const o = r?.result?.outcome;
              return <Tag color={badgeToneForStatus(o)}>{o || '-'}</Tag>;
            },
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: formatDate,
          },
        ]}
      />
      <ComplyCubeTablePagination
        page={page}
        limit={limit}
        total={total}
        rowCount={rows.length}
        loading={loading}
        onPageChange={(nextPage, nextLimit) => {
          setLimit(nextLimit);
          setPage(nextPage);
        }}
        itemName="checks"
      />
    </>
  );
}
